import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the sideways drawer menu with pane navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLElement} paneContainer - The pane container element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawerSideways extends Component {
  requiredRefs = ['details', 'paneContainer'];

  constructor() {
    super();
    /** @type {string[]} */
    this.navigationHistory = [];
    this.currentPane = 'main';
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onClick);
    this.#setupPaneNavigation();

    // Percentage movement does not require resize handling
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onClick);
    // Nothing to remove for resize
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Handle click events for navigation
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const target = event.target.closest('[data-navigate-to]');
    if (target && target instanceof HTMLElement && target.dataset.navigateTo) {
      event.preventDefault();
      event.stopPropagation();
      this.#navigateToPane(target.dataset.navigateTo, target);
      return;
    }

    const backButton = event.target.closest('[data-navigate-back]');
    if (backButton) {
      event.preventDefault();
      event.stopPropagation();
      this.#navigateBack();
      return;
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Re-apply current offset on resize to avoid rounding artifacts
   */
  // Kept for compatibility; not used when using percentage transforms
  #onResize = () => {};

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
    this.#resetNavigation();
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    if (this.navigationHistory.length > 0) {
      this.#navigateBack();
    } else {
      this.#close(this.#getDetailsElement(event));
    }
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      this.#reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(this.#reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Navigate to a specific pane
   * @param {string} paneId - The ID of the pane to navigate to
   * @param {HTMLElement} triggerElement - The element that triggered the navigation
   */
  #navigateToPane(paneId, triggerElement) {
    const targetPane = this.querySelector(`[data-pane="${paneId}"]`);
    if (!targetPane || !(targetPane instanceof HTMLElement)) return;

    // Add current pane to history
    this.navigationHistory.push(this.currentPane);

    // Update current pane
    this.currentPane = paneId;

    // Update ARIA attributes
    triggerElement.setAttribute('aria-expanded', 'true');

    // Perform the transition
    this.#transitionToPane(targetPane);
  }

  /**
   * Navigate back to the previous pane
   */
  #navigateBack() {
    if (this.navigationHistory.length === 0) return;

    const previousPaneId = this.navigationHistory.pop();
    if (!previousPaneId) return;

    const targetPane = this.querySelector(`[data-pane="${previousPaneId}"]`);

    if (!targetPane || !(targetPane instanceof HTMLElement)) return;

    // Update current pane
    this.currentPane = previousPaneId;

    // Update ARIA attributes
    const triggerButton = this.querySelector(`[data-navigate-to="${previousPaneId}"]`);
    if (triggerButton) {
      triggerButton.setAttribute('aria-expanded', 'false');
    }

    // Perform the transition
    this.#transitionToPane(targetPane, true);
  }

  /**
   * Perform the pane transition animation
   * @param {HTMLElement} targetPane - The pane to transition to
   * @param {boolean} isBackward - Whether this is a backward navigation
   */
  #transitionToPane(targetPane, isBackward = false) {
    const panesTrack = this.refs.paneContainer.querySelector('.menu-drawer__panes');
    const submenuContainer = this.querySelector('.menu-drawer__pane--submenu-container');
    if (!(panesTrack instanceof HTMLElement) || !(submenuContainer instanceof HTMLElement)) return;

    const isTargetMain = targetPane.dataset.pane === 'main';

    // Toggle visible submenu content
    const allSubmenus = submenuContainer.querySelectorAll('.menu-drawer__pane--submenu');
    allSubmenus.forEach((submenu) => {
      if (!(submenu instanceof HTMLElement)) return;
      if (!isTargetMain && submenu.dataset.pane === targetPane.dataset.pane) {
        submenu.classList.add('is-visible');
      } else {
        submenu.classList.remove('is-visible');
      }
    });

    // Slide the two-pane track using CSS custom property to avoid transform collisions
    const shiftPercent = isTargetMain ? 0 : -50;
    requestAnimationFrame(() => {
      panesTrack.style.setProperty('--panes-shift', `${shiftPercent}%`);
    });

    // Update focus to the first interactive element in the active column
    // const focusScope = isTargetMain
    //   ? this.querySelector('[data-pane="main"]')
    //   : submenuContainer.querySelector('.menu-drawer__pane--submenu.is-visible');
    // const focusableElement = focusScope?.querySelector('button, a, [tabindex]');
    // if (focusableElement && focusableElement instanceof HTMLElement) focusableElement.focus();
  }

  /**
   * Reset navigation state
   */
  #resetNavigation() {
    this.navigationHistory = [];
    this.currentPane = 'main';

    // Reset track position
    const panesTrack = this.refs.paneContainer.querySelector('.menu-drawer__panes');
    if (panesTrack instanceof HTMLElement) panesTrack.style.setProperty('--panes-shift', '0%');

    // Hide all submenus
    const submenuContainer = this.querySelector('.menu-drawer__pane--submenu-container');
    submenuContainer?.querySelectorAll('.menu-drawer__pane--submenu').forEach((submenu) => {
      if (submenu instanceof HTMLElement) submenu.classList.remove('is-visible');
    });

    // Reset ARIA attributes
    const allButtons = this.querySelectorAll('[data-navigate-to]');
    allButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Setup pane navigation event listeners
   */
  #setupPaneNavigation() {
    // Add keyboard navigation support
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' && this.navigationHistory.length > 0) {
        event.preventDefault();
        this.#navigateBack();
      }
    });
  }

  /**
   * Reset an open details element to its original state
   *
   * @param {HTMLDetailsElement} element
   */
  #reset(element) {
    element.classList.remove('menu-open');
    element.removeAttribute('open');
    element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('header-drawer-sideways')) {
  customElements.define('header-drawer-sideways', HeaderDrawerSideways);
}
